import {PhoneHQAvatar, PhoneHQIconAvatar, PhoneHQButton, PhoneHQCombobox} from 'ch3-ui-lib';
import {Colors, selectorItem} from "../../utilis/types/Types";
import {useCallback, useMemo, useState} from "react";
import {debounce} from "lodash";
import { User, useSearchUsersQuery} from "../../store/features/Users/userService";
import {
    FetchGroupsResponse,
    Group, useFetchGroupsAdminQuery,
    useFetchGroupsQuery
} from "../../store/features/groups/groupsService";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {useLocation} from "react-router-dom";

interface AddContactModalProps {
    setUsers: Function;
    setGroups: Function;
    selectedUsers: User[];
    selectedGroups: Group[];
    admin?: boolean;
}
const AddContactModal = (({ selectedUsers, selectedGroups, setUsers, setGroups, admin=false}:AddContactModalProps) => {


    const [phrase, setPhrase] = useState('');
    const [groupsQuery, setGroupsQuery] = useState<any>({});

    const userId = useSelector(selectUserId)
    const { data: users } = useSearchUsersQuery(phrase);


    const { data: groupData } = useFetchGroupsQuery({phrase: phrase, userId, owner: 'add-contact', ...groupsQuery}, { skip: admin });
    const { data: adminGroupsData } = useFetchGroupsAdminQuery({phrase: phrase, userId, owner: 'add-contact', ...groupsQuery}, { skip: !admin });

    const groupsData = admin ? adminGroupsData : groupData;

    const groups = (groupsData?.groups)?.map((group: FetchGroupsResponse) => ({
        groupId: group.group?.groupId,
        iconColor: group?.group.iconColor,
        icon: group?.group.icon,
        name: group?.group.name,
    })) as Group[];


    const onScrollEnd = () => {
        const currentData = admin ? adminGroupsData : groupData;
        const lastGroup = currentData?.groups?.at(-1);

        setGroupsQuery({
            lastName: lastGroup?.group.name ,
            lastGroupId: lastGroup?.group.groupId
        });
    }

    const [selectedUserList, setSelectedUserList] = useState(
        [...selectedUsers].sort((a, b) =>
            (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName)
        )
    );
    const [selectedGroupList, setSelectedGroupList] = useState(
        [...selectedGroups].sort((a, b) => a.name.localeCompare(b.name))
    );

    const filteredUsers = users?.filter(user => !selectedUserList.find(selectedUser => selectedUser.userId === user.userId));
    const filteredGroups = groups?.filter(group => !selectedGroupList.find(selectedGroup => selectedGroup.groupId === group.groupId));


    const userItems = filteredUsers?.map((user: User) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.userId.toString(),
        key: `user${user.userId}`,
        prefixElement: (
            <div className='ml-2'>
                <PhoneHQAvatar src={user.avatarUrl} name={user.firstName + ' ' + user.lastName} size="32px"/>
            </div>
        ),
    })) || [];

    const groupItems = filteredGroups?.map((group: Group) => ({
        label: group.name,
        value: group.groupId?.toString(),
        key: `group${group.groupId}`,
        prefixElement: (
            <div className='ml-2'>
                <PhoneHQIconAvatar icon={group?.icon} color={group.iconColor ? group.iconColor : Colors.grey} name={group.name} size="32px"/>
            </div>
        ),
    })) || [];
    const list = useMemo(() => [...userItems, ...groupItems] as selectorItem[], [filteredUsers, filteredGroups]);

    const handlePhraseChange = useCallback(
        debounce((phrase: string) => {
            setPhrase(phrase);
        }, 500),
        []
    );


    const handleChange = (item: selectorItem) => {
        if(item.key.includes('user')) {
            setSelectedUserList((prevUsers: User[]) => [...prevUsers, users?.find((user: User) => user.userId == Number(item.value)) as User]);
            setUsers([...selectedUserList, users?.find((user: User) => user.userId == Number(item.value)) ] );

        } else {
            setSelectedGroupList((prevGroups: Group[]) => {
                return [...prevGroups, groups.find((group: Group ) => group.groupId == Number(item.value)) as Group]
            });
            setGroups([...selectedGroupList, groups.find((group: Group ) => group.groupId == Number(item.value))] );
        }

    };

    const handleRemoveUser = (userId: number) => {
        setSelectedUserList((prevUsers: User[]) => prevUsers.filter((user) => user.userId !== userId));
        setUsers((prevUsers: User[]) => prevUsers.filter((user) => user.userId !== userId));
    };

    const handleRemoveGroup = (groupId: Group["groupId"]) => {
        setSelectedGroupList((prevGroups: Group[]) => prevGroups.filter((group) => group.groupId !== groupId));
        setGroups((prevGroups: Group[]) => prevGroups.filter((group) => group.groupId !== groupId));
    };


    const location = useLocation();
    const isContactsPath = location.pathname.includes('contacts');

    return (
        <div className="w-[400px]">
            <div className='text-ink-muted text-sm pb-5'>{isContactsPath? 'The following users and groups have access to this contact' : 'The following users and groups have access to this phone number'}
            </div>
            <div>
                <PhoneHQCombobox
                    label="Add users or group"
                    placeholder="Search"
                    clearOnSelect
                    onInputChange={handlePhraseChange}
                    onSelect={(item) => handleChange(item as selectorItem)}
                    onScrollEnd={onScrollEnd}
                    items={list}
                />
            </div>
            <div className='grid font-semibold gap-3 mb-8'>
                {Boolean(selectedUserList.length) && <>
                    <div className={'p-4 text-sm'}>
                        USERS
                    </div>
                    <div className="max-h-36 overflow-y-auto">
                        {[...selectedUserList].sort((a, b) =>
                            (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName)
                        ).map((user) => (
                            <div className='flex justify-between items-center' key={user.userId}>
                                <div className={'flex gap-3 items-center'}>
                                    <PhoneHQAvatar src={user.avatarUrl} name={user.firstName + ' ' + user.lastName} size="32px"/>
                                    <div>
                                        {user.firstName + ' ' + user.lastName}
                                    </div>
                                </div>
                                <PhoneHQButton variant="text-danger" onClick={() => handleRemoveUser(user.userId)} phq-id='addContactModal-removeUserBtn'>Remove</PhoneHQButton>
                            </div>
                        ))}
                    </div>
                </>
                }
                {Boolean(selectedGroupList?.length) &&
                    <>
                        <div className={'p-4 text-sm'}>
                            GROUPS
                        </div>
                        <div className="max-h-36 overflow-y-auto">
                            {[...selectedGroupList].sort((a, b) => a.name.localeCompare(b.name)).map((group) => (
                                <div className='flex justify-between items-center ' key={group.groupId}>

                                    <div className={'flex gap-3 items-center'}>
                                        <PhoneHQIconAvatar icon={group.icon  ?? 'star'} color={group.iconColor  ?? Colors.orange} size="32px"/>

                                        <div className="w-[250px] truncate">
                                            {group.name }
                                        </div>
                                    </div>
                                    <PhoneHQButton variant="text-danger" onClick={() => handleRemoveGroup(group.groupId)} phq-id='addContactModal-removeGroupBtn'>Remove</PhoneHQButton>

                                </div>
                            ))}
                        </div>
                    </>
                }
            </div>
        </div>
    )
});
export default AddContactModal;